import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-8">
        <div className="card border-rose-200">
          <h2 className="text-lg font-semibold text-rose-700">Something broke on this page.</h2>
          <p className="mt-2 text-sm text-slate-600">
            The error has been logged to the browser console.
          </p>
          <pre className="mt-3 overflow-x-auto rounded bg-slate-900 p-3 text-xs text-rose-200">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
          <button type="button" className="btn-primary mt-4" onClick={this.reset}>
            Try again
          </button>
        </div>
      </div>
    )
  }
}
